import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { BoltIcon } from '@heroicons/react/24/solid';
import { gameTypes } from '../data/GameTypes';
import { getGameTypeColors } from '../utils/gameTypeColors';
import ModalBasic from '../components/ModalBasic';
import { useCreditModal } from '../components/CreditModalContext';
import { normalizeCampaignStatus } from '../data/campaignStatus';

export default function CampaignHeader({ title, subtitle, gameType, internalName, onInternalNameSave, status, onStatusChange, primaryActions, gameCredits = 0, isStudioSubscriber = false, onNoCreditsClick, hidePublishButton = false }) {
  const navigate = useNavigate();
  const { openCreditModal } = useCreditModal();
  const [isInternalNameModalOpen, setIsInternalNameModalOpen] = useState(false);
  const [editValue, setEditValue] = useState(internalName || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const inputRef = useRef(null);

  const campaignStatus = normalizeCampaignStatus(status);
  const creditUiEnabled = !isStudioSubscriber;
  const needsCredit = creditUiEnabled && gameCredits === 0;
  const willSpendCredit = creditUiEnabled && gameCredits > 0;

  const statusConfig = {
    draft: {
      label: 'Draft',
      description: 'Editable, not shareable. Publish when you\'re ready to share the link or QR.',
    },
    published: {
      label: 'Published',
      description: 'Shareable via link and QR. Players can play. You can make tweaks anytime; complete when done.',
    },
    paused: {
      label: 'Paused',
      description: 'Game link is temporarily inactive. Resume to make it playable again.',
    },
    completed: {
      label: 'Completed',
      description: 'Campaign ended. Players see a "campaign ended" message. Restore to draft to republish.',
    },
  };
  const currentStatusConfig = statusConfig[campaignStatus] || statusConfig.draft;

  const hasNoCredits = creditUiEnabled && gameCredits === 0;

  const handlePublishClick = () => {
    if (!onStatusChange || isChangingStatus) return;
    if (hasNoCredits) {
      onNoCreditsClick?.();
      return;
    }
    setShowPublishModal(true);
  };

  const handlePublishConfirm = () => {
    handleStatusChange('published');
  };

  const handleStatusChange = async (newStatus) => {
    if (!onStatusChange || isChangingStatus) return;
    setIsChangingStatus(true);
    setShowCompleteModal(false);
    setShowPublishModal(false);
    setShowResumeModal(false);
    try {
      await onStatusChange(newStatus);
    } catch (error) {
      console.error('Failed to change status:', error);
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Update edit value when internalName changes
  useEffect(() => {
    if (!isInternalNameModalOpen) {
      setEditValue(internalName || '');
    }
  }, [internalName, isInternalNameModalOpen]);

  // Focus input when entering modal
  useEffect(() => {
    if (isInternalNameModalOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isInternalNameModalOpen]);

  const handleStartEdit = () => {
    if (onInternalNameSave) {
      setEditValue(internalName || title || '');
      setTimeout(() => setIsInternalNameModalOpen(true), 50);
    }
  };

  const handleSave = async () => {
    if (!onInternalNameSave) return;

    const trimmedValue = editValue.trim();
    if (trimmedValue === internalName) {
      setIsInternalNameModalOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      await onInternalNameSave(trimmedValue || null); // null to remove if empty
      setIsInternalNameModalOpen(false);
      try {
        const { notifySuccess } = await import('../utils/notify');
        notifySuccess(trimmedValue ? 'Internal name updated' : 'Internal name cleared');
      } catch { /* notify is best-effort */ }
    } catch (error) {
      console.error('Failed to save internal name:', error);
      try {
        const { notifyError } = await import('../utils/notify');
        notifyError && notifyError('Couldn\'t save internal name');
      } catch { /* */ }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(internalName || '');
    setIsInternalNameModalOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <>
      {/* Breadcrumb navigation */}
      <nav className="hidden sm:flex items-center gap-1 pb-2 text-sm" aria-label="Breadcrumb">
        <Link
          to="/studio/campaigns"
          className="font-semibold gch-text-secondary hover:gch-text-primary transition-colors"
        >
          Campaigns
        </Link>
        <ChevronRightIcon className="w-4 h-4 gch-text-muted shrink-0" />
        <span className="font-semibold gch-text-primary truncate max-w-[260px]">
          {internalName || title || 'Campaign'}
        </span>
      </nav>

      {/* Main Content */}
      <div className="flex flex-col gap-y-4 py-1 md:py-2">
        {/* Desktop Layout - Side by side */}
        <div className="hidden md:flex items-center justify-between">
          {/* Left side: Icon and Title */}
          <div className="flex items-center gap-x-4">
            {/* Game Type Icon */}
            {(() => {
              const gameTypeData = gameTypes.find(type => type.id === gameType);
              const colors = getGameTypeColors(gameType);
              
              return gameTypeData?.icon && (
                <div
                  className="p-2.5 rounded-lg"
                  style={{
                    background: colors?.bg,
                    border: `1px solid ${colors?.border}`
                  }}
                >
                  <gameTypeData.icon className="w-6 h-6" style={{ color: colors?.icon || 'var(--admin-accent)' }} />
                </div>
              );
            })()}

            {/* Internal name (primary) + Game title (muted) */}
            <div className="p-2">
              <div className="group flex items-start gap-2">
                <div className="flex flex-col">
                  {onInternalNameSave ? (
                    <button
                      type="button"
                      onClick={handleStartEdit}
                      className="text-left"
                      title="Edit internal name"
                      aria-label="Edit internal name"
                    >
                      <h1 
                        className="text-3xl font-semibold leading-7 tracking-tight gch-text-primary hover:text-[var(--admin-accent)] transition-colors"
                        style={{ fontFamily: '"Lexend", sans-serif' }}
                      >
                        {internalName || 'Add internal name'}
                      </h1>
                    </button>
                  ) : (
                    <h1 
                      className="text-3xl font-semibold leading-7 tracking-tight gch-text-primary"
                      style={{ fontFamily: '"Lexend", sans-serif' }}
                    >
                      {internalName || title}
                    </h1>
                  )}
                  {title && title !== internalName && (
                    <p className="mt-1 text-sm font-medium tracking-tight gch-text-muted">
                      {title}
                    </p>
                  )}
                  {subtitle && (
                    <p className="mt-0.5 text-sm font-medium tracking-tight gch-text-secondary">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Right side: Status badge + action + primary actions */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                campaignStatus === 'published' ? 'bg-emerald-500/15 text-emerald-400' :
                campaignStatus === 'paused' ? 'bg-amber-500/15 text-amber-400' :
                campaignStatus === 'completed' ? 'bg-blue-500/15 text-blue-400' :
                'bg-white/10 gch-text-secondary'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  campaignStatus === 'published' ? 'bg-emerald-400' :
                  campaignStatus === 'paused' ? 'bg-amber-400' :
                  campaignStatus === 'completed' ? 'bg-blue-400' :
                  'bg-white/40'
                }`} />
                {currentStatusConfig.label}
              </span>
              {onStatusChange && campaignStatus === 'draft' && !hidePublishButton && (
                <button
                  type="button"
                  onClick={handlePublishClick}
                  disabled={isChangingStatus}
                  title={hasNoCredits ? 'Publish' : undefined}
                  className="btn-admin-primary btn-admin-sm inline-flex items-center gap-1.5"
                >
                  {isChangingStatus ? 'Updating...' : (
                    <>
                      {needsCredit && <BoltIcon className="w-3.5 h-3.5 shrink-0" />}
                      Publish
                    </>
                  )}
                </button>
              )}
              {onStatusChange && campaignStatus === 'published' && (
                <button
                  type="button"
                  onClick={() => setShowCompleteModal(true)}
                  disabled={isChangingStatus}
                  className="btn-admin-outline btn-admin-sm inline-flex items-center gap-1.5"
                >
                  {isChangingStatus ? 'Updating...' : 'Complete'}
                </button>
              )}
              {onStatusChange && campaignStatus === 'paused' && (
                <button
                  type="button"
                  onClick={() => setShowResumeModal(true)}
                  disabled={isChangingStatus}
                  className="btn-admin-primary btn-admin-sm inline-flex items-center gap-1.5"
                >
                  {isChangingStatus ? 'Updating...' : 'Resume'}
                </button>
              )}
            </div>
            {primaryActions ? <div className="flex items-center gap-3">{primaryActions}</div> : null}
          </div>
        </div>

        {/* Mobile Layout - Stacked */}
        <div className="md:hidden space-y-3">
          {/* Mobile back link */}
          <Link to="/studio/campaigns" className="sm:hidden inline-flex items-center gap-1 text-xs font-semibold gch-text-secondary hover:gch-text-primary transition-colors -mb-1">
            <ChevronRightIcon className="w-3.5 h-3.5 rotate-180 shrink-0" />
            Campaigns
          </Link>
          <div className="flex items-center gap-x-3">
            {(() => {
              const gameTypeData = gameTypes.find(type => type.id === gameType);
              const colors = getGameTypeColors(gameType);
              return gameTypeData?.icon && (
                <div className="p-2 rounded-lg" style={{ background: colors?.bg, border: `1px solid ${colors?.border}` }}>
                  <gameTypeData.icon className="w-5 h-5" style={{ color: colors?.icon || 'var(--admin-accent)' }} />
                </div>
              );
            })()}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-0.5">
                {onInternalNameSave ? (
                  <button type="button" onClick={handleStartEdit} className="text-left" title="Edit internal name" aria-label="Edit internal name">
                    <h1 className="text-xl font-semibold leading-6 tracking-tight gch-text-primary truncate hover:text-[var(--admin-accent)] transition-colors" style={{ fontFamily: '"Lexend", sans-serif' }}>
                      {internalName || 'Add internal name'}
                    </h1>
                  </button>
                ) : (
                  <h1 className="text-xl font-semibold leading-6 tracking-tight gch-text-primary truncate" style={{ fontFamily: '"Lexend", sans-serif' }}>
                    {internalName || title}
                  </h1>
                )}
                {title && title !== internalName && (
                  <p className="text-xs font-medium tracking-tight gch-text-muted truncate">{title}</p>
                )}
                {subtitle && <p className="text-xs font-medium tracking-tight gch-text-secondary truncate">{subtitle}</p>}
              </div>
            </div>
          </div>
          {/* Status - Mobile: compact badge + subtle action */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
              campaignStatus === 'published' ? 'bg-emerald-500/15 text-emerald-400' :
              campaignStatus === 'paused' ? 'bg-amber-500/15 text-amber-400' :
              campaignStatus === 'completed' ? 'bg-blue-500/15 text-blue-400' :
              'bg-white/10 gch-text-secondary'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                campaignStatus === 'published' ? 'bg-emerald-400' :
                campaignStatus === 'paused' ? 'bg-amber-400' :
                campaignStatus === 'completed' ? 'bg-blue-400' :
                'bg-white/40'
              }`} />
              {currentStatusConfig.label}
            </span>
            {onStatusChange && campaignStatus === 'draft' && !hidePublishButton && (
              <button
                type="button"
                onClick={handlePublishClick}
                disabled={isChangingStatus}
                title={hasNoCredits ? 'Buy a credit to publish (£39)' : undefined}
                className="btn-admin-primary btn-admin-sm inline-flex items-center gap-1.5"
              >
                {isChangingStatus ? 'Updating...' : (
                  <>
                    {needsCredit && <BoltIcon className="w-3.5 h-3.5 shrink-0" />}
                    {needsCredit
                      ? 'Publish for £39'
                      : (willSpendCredit ? 'Publish (1 credit)' : 'Publish')}
                  </>
                )}
              </button>
            )}
            {onStatusChange && campaignStatus === 'published' && (
              <button
                type="button"
                onClick={() => setShowCompleteModal(true)}
                disabled={isChangingStatus}
                className="btn-admin-outline btn-admin-sm inline-flex items-center gap-1.5"
              >
                {isChangingStatus ? 'Updating...' : 'Complete'}
              </button>
            )}
            {onStatusChange && campaignStatus === 'paused' && (
              <button
                type="button"
                onClick={() => setShowResumeModal(true)}
                disabled={isChangingStatus}
                className="btn-admin-primary btn-admin-sm inline-flex items-center gap-1.5"
              >
                {isChangingStatus ? 'Updating...' : 'Resume'}
              </button>
            )}
          </div>
          {primaryActions && <div className="grid grid-cols-2 gap-3 w-full">{primaryActions}</div>}
        </div>
      </div>

      {/* Complete Campaign Confirmation Modal */}
      <ModalBasic id="complete-campaign-modal" title="Complete campaign?" modalOpen={showCompleteModal} setModalOpen={setShowCompleteModal}>
        <div className="p-6">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-[var(--admin-accent-light)] flex items-center justify-center">
              <svg className="w-6 h-6 text-[var(--admin-accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
          </div>
          <p className="gch-text-secondary text-center text-sm mb-4">
            The game link will stop working  - players will see a "campaign ended" message. The campaign moves out of your main list.
          </p>
          <div className="gch-input-ghost rounded-xl p-4 mb-6 space-y-2 border border-[var(--admin-border)]">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3l9.5 16.5H2.5L12 3z" />
              </svg>
              <span className="text-sm gch-text-secondary">Game link disabled  - players can no longer play</span>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm gch-text-secondary">All data and leaderboard preserved</span>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm gch-text-secondary">Restore to draft to republish later</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowCompleteModal(false)} className="flex-1 btn-admin-outline btn-admin-md text-center">Cancel</button>
            <button onClick={() => handleStatusChange('archived')} disabled={isChangingStatus} className="flex-1 btn-admin-secondary btn-admin-md text-center disabled:opacity-50">
              {isChangingStatus ? 'Updating...' : 'Complete'}
            </button>
          </div>
        </div>
      </ModalBasic>

      {/* Publish Campaign Confirmation Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPublishModal(false)} />
          <div className="relative gch-bg-card border border-[var(--admin-border)] rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-[var(--admin-accent-light)] flex items-center justify-center">
                <svg className="w-6 h-6 text-[var(--admin-accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-semibold gch-text-primary text-center mb-2">Publish campaign?</h3>
            <p className="gch-text-secondary text-center text-sm mb-4">
              Your share link and QR code will go live. Anyone with the link can play. You can make tweaks anytime or complete when done.
            </p>
            {creditUiEnabled && gameCredits > 0 && (
              <div className="flex items-center gap-3 p-3 mb-4 rounded-xl border border-[var(--admin-border)] bg-white/5">
                <svg className="w-4 h-4 shrink-0 text-[var(--admin-accent)]" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                </svg>
                <p className="text-xs font-medium text-[var(--admin-accent)]">
                  This will use 1 credit. You have {gameCredits} remaining.
                </p>
              </div>
            )}
            <div className="gch-input-ghost rounded-xl p-4 mb-6 space-y-2 border border-[var(--admin-border)]">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm gch-text-secondary">Share link and QR code go live</span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm gch-text-secondary">Players can play and submit scores</span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm gch-text-secondary">Make tweaks anytime; complete when done</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowPublishModal(false)} className="flex-1 btn-admin-outline btn-admin-md text-center">Cancel</button>
              {needsCredit ? (
                <button onClick={() => { setShowPublishModal(false); openCreditModal(); }} className="flex-1 btn-admin-primary btn-admin-md text-center">
                  Buy credits
                </button>
              ) : (
                <button onClick={handlePublishConfirm} disabled={isChangingStatus} className="flex-1 btn-admin-primary btn-admin-md text-center disabled:opacity-50">
                  {isChangingStatus ? 'Updating...' : 'Publish'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resume Campaign Confirmation Modal */}
      <ModalBasic id="resume-campaign-modal" title="Resume campaign?" modalOpen={showResumeModal} setModalOpen={setShowResumeModal}>
        <div className="p-6">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-[var(--admin-accent-light)] flex items-center justify-center">
              <svg className="w-6 h-6 text-[var(--admin-accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="gch-text-secondary text-center text-sm mb-4">
            Your campaign will go live again. Anyone with the link can play.
          </p>
          <div className="gch-input-ghost rounded-xl p-4 mb-6 space-y-2 border border-[var(--admin-border)]">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm gch-text-secondary">
                {creditUiEnabled ? 'No publish credit is used' : 'The campaign link goes live again'}
              </span>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm gch-text-secondary">Share link and QR code reactivate instantly</span>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm gch-text-secondary">All data and leaderboard preserved</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowResumeModal(false)} className="flex-1 btn-admin-outline btn-admin-md text-center">Cancel</button>
            <button onClick={() => { setShowResumeModal(false); handleStatusChange('published'); }} disabled={isChangingStatus} className="flex-1 btn-admin-primary btn-admin-md text-center disabled:opacity-50">
              {isChangingStatus ? 'Updating...' : 'Resume'}
            </button>
          </div>
        </div>
      </ModalBasic>

      {/* Internal Name Modal */}
      <ModalBasic
        id="internal-name-modal"
        title="Campaign name"
        modalOpen={isInternalNameModalOpen}
        setModalOpen={(open) => {
          if (!open && !isSaving) handleCancel();
          if (open) setTimeout(() => setIsInternalNameModalOpen(true), 50);
        }}
      >
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold gch-text-primary mb-2">
              Internal name
            </label>
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={title || 'Internal name'}
              maxLength={100}
              className="w-full rounded-xl text-sm transition-all duration-200 py-3 px-4 gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted focus:gch-input-ghost focus:border-[var(--admin-accent)] focus:ring-2 focus:ring-[var(--admin-accent)]/15 focus:outline-none"
              disabled={isSaving}
            />
            <p className="text-xs gch-text-secondary mt-2">
              Only you see this. Players see the public title set in the editor. Leave blank to use the public title.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="btn-admin-outline btn-admin-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="btn-admin-secondary btn-admin-md disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </ModalBasic>
    </>
  );
}
